import { createRsaaAction } from "../../components/utilities/ReduxUtils";
import type {
    GetChemicalsParams,
    GetNotebookLibraryParams,
    GetReactionChemicalsParams,
    GetReactionRouteSummariesParams,
    GetReactionRouteStagesParams,
    GetReactionRoutesParams,
    GetReactionSummariesParams,
    GetReactionsParams,
    GetStagesParams,
    PostSolventGuideRecommendationsParams,
    NotebookChemicalListResponse,
    NotebookLibrarySummaryResponse,
    NotebookReactionListResponse,
    NotebookReactionRouteListResponse,
    NotebookReactionRouteStageListResponse,
    NotebookReactionRouteSummaryListResponse,
    NotebookReactionSummaryListResponse,
    Reaction,
    ReactionChemical,
    ReactionChemicalRole,
    ReactionRoute,
    ReactionRouteStage,
    ReactionRouteInferRequest,
    ReactionRouteStageDependency,
    ReactionRouteValidationResult,
    ReactionConditions,
} from "../../data/models/schemas";

// Centralized Type Definitions
export type IReactionChemicalType = ReactionChemicalRole;

export type IReactionChemical = ReactionChemical;

export interface ILibraryChemical {
    ui_id: string;
    viridischem_id?: number;
    name: string;
    description?: string;
    smiles?: string;
    mass_value?: number;
    mass_unit?: string;
    properties?: Record<string, unknown>;
    created_at?: string;
    updated_at?: string;
}

// Alias for backward compatibility - prefer ILibraryChemical for new code
export type IChemical = ILibraryChemical;

export type ILibraryReaction = Reaction & {
    chemicals?: IReactionChemical[];
    created_at?: string;
    updated_at?: string;
};

// Base IReaction interface used by components
export type IReaction = ILibraryReaction;

// Extended IReaction with additional condition fields for analysis components
// TODO(orval-drift): temperature/time/yield are legacy UI fields not in schema.
export type IReactionWithConditions = ILibraryReaction & {
    conditions?: ReactionConditions;
    temperature?: string;
    time?: string;
    yield?: number;
};

export type IReactionChemicalInput = ReactionChemical;

export type ILibraryMultiStepReactionDependency =
    ReactionRouteStageDependency & {
        // UI-only display metadata when available.
        intermediateSmiles?: string;
        intermediateName?: string;
    };

export type ILibraryMultiStepReaction = ReactionRoute & {
    // Analyzer UI keeps a resolved stage->reaction map in-memory.
    reactions?: Record<string, ILibraryReaction>;
    dependencies?: ILibraryMultiStepReactionDependency[];
};

export type ILibraryReactionRouteStage = ReactionRouteStage & {
    // Optional route metadata returned by stage list endpoint variants.
    route_ui_id?: string;
    route_name?: string;
};

// API contract type for reaction-routes request/response payloads.
export type IReactionRouteApi = ReactionRoute;

export interface IReactionType {
    value: string;
    label: string;
}

// Generated endpoint param types.
export type ILibraryChemicalsQueryParams = GetChemicalsParams;
export type ILibrarySummaryQueryParams = GetNotebookLibraryParams;
export type ILibraryReactionsQueryParams = GetReactionsParams;
export type ILibraryReactionSummariesQueryParams = GetReactionSummariesParams;
export type ILibraryRoutesQueryParams = GetReactionRoutesParams;
export type ILibraryRouteSummariesQueryParams = GetReactionRouteSummariesParams;
export type ILibraryStagesQueryParams = GetStagesParams;
export type IReactionChemicalsQueryParams = GetReactionChemicalsParams;
export type IReactionRouteStagesQueryParams = GetReactionRouteStagesParams;
export type ISolventGuideRecommendationsQueryParams =
    PostSolventGuideRecommendationsParams;

// Standardized Modal Prop Interfaces
export interface BaseModalProps {
    show: boolean;
    onHide: () => void;
    loading?: boolean;
}

export interface ConfirmationModalProps extends BaseModalProps {
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    confirmVariant?: "danger" | "primary" | "warning";
    itemType?: string;
    itemName?: string;
}

export interface FormModalProps extends BaseModalProps {
    onSubmit: () => void;
    isValid?: boolean;
    submitText?: string;
}

export interface ViewModalProps extends BaseModalProps {
    size?: "sm" | "lg" | "xl";
}

// Action Types
export const LOAD_CHEMICALS = "LOAD_CHEMICALS";
export const CREATE_CHEMICAL = "CREATE_CHEMICAL";
export const UPDATE_CHEMICAL = "UPDATE_CHEMICAL";
export const DELETE_CHEMICAL = "DELETE_CHEMICAL";

export const LOAD_REACTIONS = "LOAD_REACTIONS";
export const LOAD_LIBRARY_SUMMARY = "LOAD_LIBRARY_SUMMARY";
export const LOAD_REACTION_SUMMARIES = "LOAD_REACTION_SUMMARIES";
export const LOAD_REACTION_ROUTE_SUMMARIES = "LOAD_REACTION_ROUTE_SUMMARIES";
export const LOAD_REACTION_TYPES = "LOAD_REACTION_TYPES";
export const CREATE_REACTION = "CREATE_REACTION";
export const UPDATE_REACTION = "UPDATE_REACTION";
export const DELETE_REACTION = "DELETE_REACTION";
export const ADD_CHEMICAL_TO_REACTION = "ADD_CHEMICAL_TO_REACTION";
export const REMOVE_CHEMICAL_FROM_REACTION = "REMOVE_CHEMICAL_FROM_REACTION";
export const LOAD_MULTI_STEP_REACTIONS = "LOAD_MULTI_STEP_REACTIONS";
export const LOAD_REACTION_ROUTE_STAGES = "LOAD_REACTION_ROUTE_STAGES";
export const CREATE_MULTI_STEP_REACTION = "CREATE_MULTI_STEP_REACTION";
export const UPDATE_MULTI_STEP_REACTION = "UPDATE_MULTI_STEP_REACTION";
export const DELETE_MULTI_STEP_REACTION = "DELETE_MULTI_STEP_REACTION";
export const INFER_MULTI_STEP_REACTION = "INFER_MULTI_STEP_REACTION";
export const VALIDATE_MULTI_STEP_REACTION = "VALIDATE_MULTI_STEP_REACTION";
export const VALIDATE_SAVED_MULTI_STEP_REACTION =
    "VALIDATE_SAVED_MULTI_STEP_REACTION";

// Types are now defined directly in this file above

export interface ICreateChemicalRequest {
    name: string;
    description?: string;
    smiles?: string;
    viridischem_id?: number;
    mass_value?: number;
    mass_unit?: string;
    properties?: Record<string, unknown>;
}

export type ICreateReactionRequest = Reaction;

export type IUpdateReactionRequest = Reaction & { ui_id: string };

export type IAddChemicalToReactionRequest = ReactionChemical;

// Chemical Actions
export const loadChemicals = createRsaaAction<
    void,
    NotebookChemicalListResponse
>({
    path: "notebooks/chemicals",
    method: "GET",
    type: LOAD_CHEMICALS,
});

export const createChemical = createRsaaAction<
    ICreateChemicalRequest,
    ILibraryChemical
>({
    path: "notebooks/chemicals",
    method: "POST",
    type: CREATE_CHEMICAL,
});

export const updateChemical = createRsaaAction<
    ILibraryChemical,
    ILibraryChemical
>({
    path: "notebooks/chemicals/:chemicalId",
    method: "PUT",
    type: UPDATE_CHEMICAL,
});

export const deleteChemical = createRsaaAction<void, void>({
    path: "notebooks/chemicals/:chemicalId",
    method: "DELETE",
    type: DELETE_CHEMICAL,
});

// Reaction Actions
export const loadReactions = createRsaaAction<
    void,
    NotebookReactionListResponse
>({
    path: "notebooks/reactions",
    method: "GET",
    type: LOAD_REACTIONS,
});

export const loadLibrarySummary = createRsaaAction<
    void,
    NotebookLibrarySummaryResponse
>({
    path: "notebooks/library",
    method: "GET",
    type: LOAD_LIBRARY_SUMMARY,
});

export const loadReactionSummaries = createRsaaAction<
    void,
    NotebookReactionSummaryListResponse
>({
    path: "notebooks/reactions/summaries",
    method: "GET",
    type: LOAD_REACTION_SUMMARIES,
});

export const loadReactionRouteSummaries = createRsaaAction<
    void,
    NotebookReactionRouteSummaryListResponse
>({
    path: "notebooks/reaction-routes/summaries",
    method: "GET",
    type: LOAD_REACTION_ROUTE_SUMMARIES,
});

export const loadReactionTypes = createRsaaAction<void, IReactionType[]>({
    path: "reactions/types",
    method: "GET",
    type: LOAD_REACTION_TYPES,
});

export const createReaction = createRsaaAction<
    ICreateReactionRequest,
    ILibraryReaction
>({
    path: "notebooks/reactions",
    method: "POST",
    type: CREATE_REACTION,
});

export const updateReaction = createRsaaAction<
    IUpdateReactionRequest,
    ILibraryReaction
>({
    path: "notebooks/reactions/:reactionId",
    method: "PUT",
    type: UPDATE_REACTION,
});

export const deleteReaction = createRsaaAction<void, void>({
    path: "notebooks/reactions/:reactionId",
    method: "DELETE",
    type: DELETE_REACTION,
});

export const addChemicalToReaction = createRsaaAction<
    IAddChemicalToReactionRequest,
    IReactionChemical
>({
    path: "notebooks/reactions/:reactionId/chemicals",
    method: "POST",
    type: ADD_CHEMICAL_TO_REACTION,
});

export const removeChemicalFromReaction = createRsaaAction<void, void>({
    path: "notebooks/reactions/:reactionId/chemicals/:chemicalId",
    method: "DELETE",
    type: REMOVE_CHEMICAL_FROM_REACTION,
});

export const loadMultiStepReactions = createRsaaAction<
    void,
    NotebookReactionRouteListResponse
>({
    path: "notebooks/reaction-routes",
    method: "GET",
    type: LOAD_MULTI_STEP_REACTIONS,
});

export const loadReactionRouteStages = createRsaaAction<
    void,
    NotebookReactionRouteStageListResponse
>({
    path: "notebooks/stages",
    method: "GET",
    type: LOAD_REACTION_ROUTE_STAGES,
});

export const createMultiStepReaction = createRsaaAction<
    IReactionRouteApi,
    ILibraryMultiStepReaction
>({
    path: "notebooks/reaction-routes",
    method: "POST",
    type: CREATE_MULTI_STEP_REACTION,
});

export const updateMultiStepReaction = createRsaaAction<
    IReactionRouteApi,
    ILibraryMultiStepReaction
>({
    path: "notebooks/reaction-routes/:routeUiId",
    method: "PUT",
    type: UPDATE_MULTI_STEP_REACTION,
});

export const deleteMultiStepReaction = createRsaaAction<void, void>({
    path: "notebooks/reaction-routes/:routeUiId",
    method: "DELETE",
    type: DELETE_MULTI_STEP_REACTION,
});

export const inferMultiStepReaction = createRsaaAction<
    ReactionRouteInferRequest,
    ILibraryMultiStepReaction
>({
    path: "notebooks/reaction-routes/infer",
    method: "POST",
    type: INFER_MULTI_STEP_REACTION,
});

export const validateMultiStepReaction = createRsaaAction<
    ReactionRoute,
    ReactionRouteValidationResult
>({
    path: "notebooks/reaction-routes/validate",
    method: "POST",
    type: VALIDATE_MULTI_STEP_REACTION,
});

export const validateSavedMultiStepReaction = createRsaaAction<
    void,
    ReactionRouteValidationResult
>({
    path: "notebooks/reaction-routes/:routeUiId/validate",
    method: "POST",
    type: VALIDATE_SAVED_MULTI_STEP_REACTION,
});
